import { _ as Swarm, a as createRtcTransportFactory, b as ConnectionState, c as MemoryTransportFactory, d as SwarmNetworkManagerService, f as ConnectionLog, g as SwarmMapper, h as MAX_CONCURRENT_INITIATING_CONNECTIONS, i as RtcTransportProxyFactory, l as FullyConnectedTopology, m as ConnectionLimiter, n as RtcTransportService, o as TransportKind, p as EventType, r as RtcTransportProxy, s as MemoryTransport, t as createTeleportProtocolFactory, u as SwarmNetworkManager, v as SwarmMessenger, y as Connection } from "./chunk-wire-protocol.mjs";
import { asyncTimeout } from "@dxos/async";
import { isNonNullable } from "@dxos/util";
import { invariant } from "@dxos/invariant";
import { log } from "@dxos/log";
//#region src/signal/ice.ts
var __dxlog_file$2 = "/__w/dxos/dxos/packages/core/mesh/network-manager/src/signal/ice.ts";
var createIceProvider = (iceProviders) => {
	let cachedIceServers;
	return { getIceServers: async () => {
		if (cachedIceServers) return cachedIceServers;
		cachedIceServers = (await Promise.all(iceProviders.map(({ urls }) => asyncTimeout(fetch(urls, { method: "GET" }), 1e4).then((response) => response.json()).catch((err) => {
			if (!(typeof window !== "undefined" && window.location.href.includes("localhost"))) log.error("Failed to fetch ICE servers from provider", {
				urls,
				err
			}, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file$2,
				L: 30,
				S: void 0
			});
		})))).filter(isNonNullable).map(({ iceServers }) => iceServers).flat();
		return cachedIceServers;
	} };
};
//#endregion
//#region src/topology/mmst-topology.ts
var __dxlog_file$1 = "/__w/dxos/dxos/packages/core/mesh/network-manager/src/topology/mmst-topology.ts";
var MIN_UPDATE_INTERVAL = 1e3 * 10;
var MAX_CHANGES_PER_UPDATE = 1;
var MMSTTopology = class {
	_originateConnections;
	_maxPeers;
	_sampleSize;
	_controller;
	_sampleCollected = false;
	_lastAction = /* @__PURE__ */ new Date(0);
	constructor({ originateConnections = 2, maxPeers = 4, sampleSize = 10 } = {}) {
		this._originateConnections = originateConnections;
		this._maxPeers = maxPeers;
		this._sampleSize = sampleSize;
	}
	init(controller) {
		invariant(!this._controller, "Already initialized", {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file$1,
			L: 49,
			S: this,
			A: ["!this._controller", "'Already initialized'"]
		});
		this._controller = controller;
	}
	update() {
		invariant(this._controller, "Not initialized", {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file$1,
			L: 54,
			S: this,
			A: ["this._controller", "'Not initialized'"]
		});
		const { connected, candidates } = this._controller.getState();
		if (this._sampleCollected || connected.length > this._maxPeers || candidates.length > 0) {
			log("Running the algorithm.", void 0, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file$1,
				L: 58,
				S: this
			});
			this._sampleCollected = true;
			this._runAlgorithm();
		}
	}
	forceUpdate() {
		this._lastAction = /* @__PURE__ */ new Date(0);
		this.update();
	}
	async onOffer(peer) {
		invariant(this._controller, "Not initialized", {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file$1,
			L: 70,
			S: this,
			A: ["this._controller", "'Not initialized'"]
		});
		const { connected } = this._controller.getState();
		const accept = connected.length < this._maxPeers;
		log(`Offer ${peer} accept=${accept}`, void 0, {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file$1,
			L: 73,
			S: this
		});
		return accept;
	}
	async destroy() {}
	_runAlgorithm() {
		invariant(this._controller, "Not initialized", {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file$1,
			L: 82,
			S: this,
			A: ["this._controller", "'Not initialized'"]
		});
		const { connected, candidates, ownPeerId } = this._controller.getState();
		if (connected.length > this._maxPeers) {
			log(`disconnect ${connected.length - this._maxPeers} peers.`, void 0, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file$1,
				L: 88,
				S: this
			});
			const sorted = sortByXorDistance(connected, ownPeerId).reverse().slice(0, this._maxPeers - connected.length);
			invariant(sorted.length === 0, void 0, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file$1,
				L: 92,
				S: this,
				A: ["sorted.length === 0", ""]
			});
			if (sorted.length > MAX_CHANGES_PER_UPDATE) log(`want to disconnect ${sorted.length} peers but limited to ${MAX_CHANGES_PER_UPDATE}`, void 0, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file$1,
				L: 95,
				S: this
			});
			if (Date.now() - this._lastAction.getTime() > MIN_UPDATE_INTERVAL) {
				for (const peer of sorted.slice(0, MAX_CHANGES_PER_UPDATE)) {
					log(`Disconnect ${peer}.`, void 0, {
						"~LogMeta": "~LogMeta",
						F: __dxlog_file$1,
						L: 100,
						S: this
					});
					this._controller.disconnect(peer);
				}
				this._lastAction = /* @__PURE__ */ new Date();
			} else log("rate limited disconnect", void 0, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file$1,
				L: 105,
				S: this
			});
		} else if (connected.length < this._originateConnections) {
			log(`connect ${this._originateConnections - connected.length} peers.`, void 0, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file$1,
				L: 109,
				S: this
			});
			const sorted = sortByXorDistance(candidates.sort(() => Math.random() - .5).slice(0, this._sampleSize), ownPeerId).slice(0, this._originateConnections - connected.length);
			if (sorted.length > MAX_CHANGES_PER_UPDATE) log(`want to connect ${sorted.length} peers but limited to ${MAX_CHANGES_PER_UPDATE}`, void 0, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file$1,
				L: 114,
				S: this
			});
			if (Date.now() - this._lastAction.getTime() > MIN_UPDATE_INTERVAL) {
				for (const peer of sorted.slice(0, MAX_CHANGES_PER_UPDATE)) {
					log(`Connect ${peer}.`, void 0, {
						"~LogMeta": "~LogMeta",
						F: __dxlog_file$1,
						L: 118,
						S: this
					});
					this._controller.connect(peer);
				}
				this._lastAction = /* @__PURE__ */ new Date();
			} else log("rate limited connect", void 0, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file$1,
				L: 123,
				S: this
			});
		}
	}
	toString() {
		return "MMSTTopology";
	}
};
var sortByXorDistance = (keys, reference) => {
	const sorted = keys.sort((a, b) => {
		return compareXor(distXor(a.asBuffer(), reference.asBuffer()), distXor(b.asBuffer(), reference.asBuffer()));
	});
	log("Sorted keys", {
		keys,
		reference,
		sorted
	}, {
		"~LogMeta": "~LogMeta",
		F: __dxlog_file$1,
		L: 137,
		S: void 0
	});
	return sorted;
};
var distXor = (a, b) => {
	const maxLength = Math.max(a.length, b.length);
	const result = Buffer.allocUnsafe(maxLength);
	for (let i = 0; i < maxLength; i++) result[i] = (a[i] || 0) ^ (b[i] || 0);
	return result;
};
var compareXor = (a, b) => {
	const maxLength = Math.max(a.length, b.length);
	for (let i = 0; i < maxLength; i++) {
		if ((a[i] || 0) === (b[i] || 0)) continue;
		return (a[i] || 0) < (b[i] || 0) ? -1 : 1;
	}
	return 0;
};
//#endregion
//#region src/topology/star-topology.ts
var __dxlog_file = "/__w/dxos/dxos/packages/core/mesh/network-manager/src/topology/star-topology.ts";
var StarTopology = class {
	_centralPeer;
	_controller;
	constructor(_centralPeer) {
		this._centralPeer = _centralPeer;
	}
	toString() {
		return `StarTopology(${this._centralPeer.truncate()})`;
	}
	init(controller) {
		invariant(!this._controller, "Already initialized.", {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file,
			L: 21,
			S: this,
			A: ["!this._controller", "'Already initialized.'"]
		});
		this._controller = controller;
	}
	update() {
		invariant(this._controller, "Not initialized.", {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file,
			L: 26,
			S: this,
			A: ["this._controller", "'Not initialized.'"]
		});
		const { candidates, connected, ownPeerId } = this._controller.getState();
		if (!ownPeerId.equals(this._centralPeer)) {
			log("leaf peer dropping all connections apart from central peer.", void 0, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file,
				L: 29,
				S: this
			});
			for (const peer of connected) if (!peer.equals(this._centralPeer)) {
				log("dropping connection", { peer }, {
					"~LogMeta": "~LogMeta",
					F: __dxlog_file,
					L: 34,
					S: this
				});
				this._controller.disconnect(peer);
			}
		}
		for (const peer of candidates) if (peer.equals(this._centralPeer) || ownPeerId.equals(this._centralPeer)) {
			log("connecting to peer", { peer }, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file,
				L: 43,
				S: this
			});
			this._controller.connect(peer);
		}
	}
	async onOffer(peer) {
		invariant(this._controller, "Not initialized.", {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file,
			L: 50,
			S: this,
			A: ["this._controller", "'Not initialized.'"]
		});
		const { ownPeerId } = this._controller.getState();
		log("offer", {
			peer,
			isCentral: peer.equals(this._centralPeer),
			isSelfCentral: ownPeerId.equals(this._centralPeer)
		}, {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file,
			L: 52,
			S: this
		});
		return ownPeerId.equals(this._centralPeer) || peer.equals(this._centralPeer);
	}
	async destroy() {}
};
//#endregion
export { Connection, ConnectionLimiter, ConnectionLog, ConnectionState, EventType, FullyConnectedTopology, MAX_CONCURRENT_INITIATING_CONNECTIONS, MMSTTopology, MemoryTransport, MemoryTransportFactory, RtcTransportProxy, RtcTransportProxyFactory, RtcTransportService, StarTopology, Swarm, SwarmMapper, SwarmMessenger, SwarmNetworkManager, SwarmNetworkManagerService, TransportKind, createIceProvider, createRtcTransportFactory, createTeleportProtocolFactory };

//# sourceMappingURL=index.mjs.map